import { lazy, Suspense } from "react";
import { Outlet } from "react-router-dom";
import { Head, type RouteRecord } from "vite-react-ssg";

import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Analytics } from "@/components/Analytics";
import { AppConfirmProvider } from "@/components/AppConfirmProvider";

import { SIGNS } from "@/lib/signs";
import {
  RITUAL_CATEGORIAS,
  SUENO_CATEGORIAS,
} from "@/lib/content/categories";
import { SERVICE_SLUGS } from "@/lib/serviceCatalog";
import { FLAT_RITUAL_SLUGS } from "@/lib/ritualRoutes";
import { isContentSuenoSsable, isDreamSeoSsable } from "@/lib/seoRedirects";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const lazyWithRetry = <T extends React.ComponentType<any>>(
  loader: () => Promise<{ default: T }>
) => {
  return lazy(async () => {
    try {
      return await loader();
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      const isChunkError =
        /Failed to fetch/i.test(errorMsg) ||
        /load failed/i.test(errorMsg) ||
        /chunk/i.test(errorMsg) ||
        /dynamically imported/i.test(errorMsg);

      if (isChunkError && typeof window !== "undefined") {
        console.warn("Chunk load failed, reloading page...", error);
        window.location.reload();
        return new Promise(() => {}) as never;
      }
      throw error;
    }
  });
};

const Toasters = lazyWithRetry(() => import("@/components/Toasters"));

const DEFAULT_TITLE =
  "Senderos Esotéricos — Tarot, Carta Astral y Horóscopo Diario";
const DEFAULT_DESCRIPTION =
  "Consultas espirituales, tarot, carta astral personalizada con IA, horóscopo diario y rituales para amor, dinero y trabajo.";
const RootLayout = () => (
  <ErrorBoundary>
    <Head>
      <title>{DEFAULT_TITLE}</title>
      <meta name="description" content={DEFAULT_DESCRIPTION} />
      <link rel="canonical" href="https://consultasespirituales.net/" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={DEFAULT_TITLE} />
      <meta property="og:description" content={DEFAULT_DESCRIPTION} />
      <meta name="twitter:title" content={DEFAULT_TITLE} />
      <meta name="twitter:description" content={DEFAULT_DESCRIPTION} />
    </Head>
    <Analytics />
    <TooltipProvider>
      <Suspense fallback={null}>
        <Toasters />
      </Suspense>
      <AppConfirmProvider>
        <AuthProvider>
          <Outlet />
        </AuthProvider>
      </AppConfirmProvider>
    </TooltipProvider>
  </ErrorBoundary>
);

const loadDefault = <T,>(
  loader: () => Promise<{ default: T }>
) => async () => {
  try {
    const mod = await loader();
    return { Component: mod.default as never };
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    const isChunkError =
      /Failed to fetch/i.test(errorMsg) ||
      /load failed/i.test(errorMsg) ||
      /chunk/i.test(errorMsg) ||
      /dynamically imported/i.test(errorMsg);

    if (isChunkError && typeof window !== "undefined") {
      console.warn("Dynamic import failed, reloading page...", error);
      window.location.reload();
      return new Promise(() => {}) as never;
    }
    throw error;
  }
};

export const routes: RouteRecord[] = [
  {
    path: "/",
    Component: RootLayout,
    entry: "src/App.tsx",
    children: [
      {
        index: true,
        lazy: loadDefault(() => import("./pages/Index")),
      },

      {
        path: "compatibilidad-de-signos",
        lazy: loadDefault(() => import("./pages/CompatibilidadSeoPage")),
      },
      {
        path: "ascendente",
        lazy: loadDefault(() => import("./pages/AscendenteSeoPage")),
      },
      {
        path: "ascendente/:signo",
        lazy: loadDefault(() => import("./pages/AscendenteSeoPage")),
        getStaticPaths: () => SIGNS.map((s) => `ascendente/${s.slug}`),
      },
      {
        path: "horoscopo-chino",
        lazy: loadDefault(() => import("./pages/HoroscopoChinoSeoPage")),
      },
      {
        path: "horoscopo-chino/:animal",
        lazy: loadDefault(() => import("./pages/HoroscopoChinoSeoPage")),
        getStaticPaths: async () => {
          const { HOROSCOPO_CHINO_SLUGS } = await import("@/lib/horoscopoChinoSeo");
          return HOROSCOPO_CHINO_SLUGS.map((slug) => `horoscopo-chino/${slug}`);
        },
      },
      {
        path: "horoscopo",
        lazy: loadDefault(() => import("./pages/Horoscopo")),
      },
      {
        path: "horoscopo/mensual/:mes",
        lazy: loadDefault(() => import("./pages/HoroscopoMensualPeriodo")),
        getStaticPaths: async () => {
          const { getCurrentMonthSlug } = await import("@/lib/dateUtils");
          return [`horoscopo/mensual/${getCurrentMonthSlug()}`];
        },
      },
      {
        path: "horoscopo/semanal/:semana",
        lazy: loadDefault(() => import("./pages/HoroscopoSemanalPeriodo")),
        getStaticPaths: async () => {
          const { getCurrentWeekSlug } = await import("@/lib/dateUtils");
          return [`horoscopo/semanal/${getCurrentWeekSlug()}`];
        },
      },
      {
        path: "horoscopo/archivo",
        lazy: loadDefault(() => import("./pages/HoroscopoArchivo")),
      },
      {
        path: "horoscopo/:signo",
        lazy: loadDefault(() => import("./pages/HoroscopoSigno")),
        getStaticPaths: () => SIGNS.map((s) => `horoscopo/${s.slug}`),
      },

      {
        path: "carta-astral",
        lazy: loadDefault(() => import("./pages/CartaAstral")),
      },
      {
        path: "que-es-la-carta-astral",
        lazy: loadDefault(() => import("./pages/AstrologyGuidePage")),
      },
      {
        path: "como-interpretar-carta-natal",
        lazy: loadDefault(() => import("./pages/AstrologyGuidePage")),
      },
      {
        path: "carta-astral-sin-hora-de-nacimiento",
        lazy: loadDefault(() => import("./pages/AstrologyGuidePage")),
      },
      {
        path: "carta-astral-gratis-vs-premium",
        lazy: loadDefault(() => import("./pages/CartaAstralGratisVsPremium")),
      },
      {
        path: "carta-astral/muestra",
        lazy: loadDefault(() => import("./pages/MuestraCartaAstral")),
      },
      {
        path: "carta-astral-gratis",
        lazy: async () => {
          const mod = await import("./pages/CartaAstralGratis");
          return {
            Component: () => <mod.default variant="default" />,
          };
        },
      },
      {
        path: "carta-astral-gratis-completa-explicada",
        lazy: async () => {
          const mod = await import("./pages/CartaAstralGratis");
          return {
            Component: () => <mod.default variant="completa-explicada" />,
          };
        },
      },
      {
        path: "carta-astral-gratis-chile",
        lazy: async () => {
          const mod = await import("./pages/CartaAstralGratis");
          return {
            Component: () => <mod.default variant="chile" />,
          };
        },
      },

      {
        path: "tarot-online",
        lazy: loadDefault(() => import("./pages/TarotSeoPage")),
      },
      {
        path: "tarot-online-gratis",
        lazy: loadDefault(() => import("./pages/TarotSeoPage")),
      },
      {
        path: "tarot-si-o-no",
        lazy: loadDefault(() => import("./pages/TarotSeoPage")),
      },
      {
        path: "tarot-del-amor",
        lazy: loadDefault(() => import("./pages/TarotSeoPage")),
      },
      {
        path: "tarot-3-cartas",
        lazy: loadDefault(() => import("./pages/TarotSeoPage")),
      },
      {
        path: "tarot-una-pregunta",
        lazy: loadDefault(() => import("./pages/TarotSeoPage")),
      },
      {
        path: "tarot-que-siente-por-mi",
        lazy: loadDefault(() => import("./pages/TarotSeoPage")),
      },
      {
        path: "tarot-online-con-ia",
        lazy: loadDefault(() => import("./pages/TarotSeoPage")),
      },
      {
        path: "como-leer-el-tarot",
        lazy: loadDefault(() => import("./pages/TarotSeoPage")),
      },
      {
        path: "tarot/arcanos-mayores",
        lazy: loadDefault(() => import("./pages/TarotArcanosSeoPage")),
      },
      {
        path: "tarot/arcanos-mayores/:slug",
        lazy: loadDefault(() => import("./pages/TarotArcanosSeoPage")),
        getStaticPaths: async () => {
          const { TAROT_ARCANO_SLUGS } = await import("@/lib/tarotArcanosSeo");
          return TAROT_ARCANO_SLUGS.map((slug) => `tarot/arcanos-mayores/${slug}`);
        },
      },

      {
        path: "numerologia",
        lazy: loadDefault(() => import("./pages/NumerologiaSeoPage")),
      },
      {
        path: "numerologia/camino-de-vida",
        lazy: loadDefault(() => import("./pages/NumerologiaSeoPage")),
      },
      {
        path: "numerologia/numero-del-nombre",
        lazy: loadDefault(() => import("./pages/NumerologiaSeoPage")),
      },
      {
        path: "numerologia/ano-personal",
        lazy: loadDefault(() => import("./pages/NumerologiaSeoPage")),
      },
      {
        path: "numerologia/1111",
        lazy: loadDefault(() => import("./pages/NumerologiaSeoPage")),
      },
      {
        path: "numerologia/333",
        lazy: loadDefault(() => import("./pages/NumerologiaSeoPage")),
      },
      {
        path: "numerologia/444",
        lazy: loadDefault(() => import("./pages/NumerologiaSeoPage")),
      },
      {
        path: "numerologia/555",
        lazy: loadDefault(() => import("./pages/NumerologiaSeoPage")),
      },
      {
        path: "numerologia/777",
        lazy: loadDefault(() => import("./pages/NumerologiaSeoPage")),
      },

      {
        path: "servicios",
        lazy: loadDefault(() => import("./pages/Servicios")),
      },
      {
        path: "servicios/:slug",
        lazy: loadDefault(() => import("./pages/ServicioDetalle")),
        getStaticPaths: () => SERVICE_SLUGS.map((slug) => `servicios/${slug}`),
      },
      {
        path: "blog/:slug",
        lazy: loadDefault(() => import("./pages/BlogSeoFunnel")),
      },
      {
        path: "terminos-y-condiciones",
        lazy: loadDefault(() => import("./pages/TerminosCondiciones")),
      },
      {
        path: "politica-de-privacidad",
        lazy: loadDefault(() => import("./pages/PoliticaPrivacidad")),
      },

      {
        path: "rituales",
        lazy: loadDefault(() => import("./pages/Rituales")),
      },
      {
        path: "rituales/rituales-espirituales-para-ano-nuevo",
        lazy: async () => {
          const mod = await import("./pages/RitualDetalle");
          return {
            Component: () => (
              <mod.default
                overrideCategory="suerte"
                overrideSlug="rituales-espirituales-para-ano-nuevo"
              />
            ),
          };
        },
      },
      {
        path: "rituales/ritual-para-limpiar-energias",
        lazy: async () => {
          const mod = await import("./pages/RitualDetalle");
          return {
            Component: () => (
              <mod.default
                overrideCategory="limpieza"
                overrideSlug="ritual-para-limpiar-energias"
              />
            ),
          };
        },
      },
      {
        path: "rituales/ritual-de-luna-llena",
        lazy: async () => {
          const mod = await import("./pages/RitualDetalle");
          return {
            Component: () => (
              <mod.default
                overrideCategory="suerte"
                overrideSlug="ritual-de-luna-llena"
              />
            ),
          };
        },
      },
      {
        path: "rituales/ritual-para-abrir-caminos",
        lazy: async () => {
          const mod = await import("./pages/RitualDetalle");
          return {
            Component: () => (
              <mod.default
                overrideCategory="trabajo"
                overrideSlug="ritual-para-abrir-caminos"
              />
            ),
          };
        },
      },
      {
        path: "rituales/ritual-de-proteccion",
        lazy: async () => {
          const mod = await import("./pages/RitualDetalle");
          return {
            Component: () => (
              <mod.default
                overrideCategory="proteccion"
                overrideSlug="ritual-de-proteccion"
              />
            ),
          };
        },
      },
      {
        path: "rituales/:categoria",
        lazy: loadDefault(() => import("./pages/RitualCategoria")),
        getStaticPaths: () =>
          RITUAL_CATEGORIAS.map((c) => `rituales/${c.slug}`),
      },
      {
        path: "rituales/:categoria/:slug",
        lazy: loadDefault(() => import("./pages/RitualDetalle")),
        getStaticPaths: async () => {
          if (!import.meta.env.SSR) return [];
          const { getAllRituales } = await import("@/lib/content/loader");
          return getAllRituales()
            .filter((r) => !FLAT_RITUAL_SLUGS.has(r.slug))
            .map((r) => `rituales/${r.categoria}/${r.slug}`);
        },
      },

      {
        path: "que-significa-sonar-con",
        lazy: loadDefault(() => import("./pages/SuenoHub")),
      },
      {
        path: "que-significa-sonar-con/:categoria",
        lazy: loadDefault(() => import("./pages/SuenoCategoria")),
        getStaticPaths: () =>
          SUENO_CATEGORIAS.map((c) => `que-significa-sonar-con/${c.slug}`),
      },
      {
        path: "que-significa-sonar-con/:categoria/:slug",
        lazy: loadDefault(() => import("./pages/SuenoDetalle")),
        getStaticPaths: async () => {
          if (!import.meta.env.SSR) return [];
          const { getAllSuenos } = await import("@/lib/content/loader");
          return getAllSuenos()
            .filter((s) => isContentSuenoSsable(s.categoria, s.slug))
            .map((s) => `que-significa-sonar-con/${s.categoria}/${s.slug}`);
        },
      },

      {
        path: "significado-de-los-suenos",
        lazy: loadDefault(() => import("./pages/SignificadoSuenosHub")),
      },
      {
        path: "significado-de-los-suenos/orden-alfabetico",
        lazy: loadDefault(() => import("./pages/SignificadoSuenosAlfabetico")),
      },
      {
        path: "significado-de-los-suenos/tipos-de-suenos-y-su-significado",
        lazy: loadDefault(() => import("./pages/SignificadoSuenosTipos")),
      },
      {
        path: "significado-de-los-suenos/significado-oculto-de-los-suenos",
        lazy: loadDefault(() => import("./pages/SignificadoSuenosOculto")),
      },
      {
        path: "significado-de-los-suenos/sonar-que-se-caen-los-dientes",
        lazy: loadDefault(() => import("./pages/SonarQueSeCaenLosDientes")),
      },
      {
        path: "significado-de-los-suenos/sonar-con-serpientes",
        lazy: loadDefault(() => import("./pages/SonarConSerpientes")),
      },
      {
        path: "significado-de-los-suenos/sonar-con-aranas",
        lazy: loadDefault(() => import("./pages/SonarConAranas")),
      },
      {
        path: "significado-de-los-suenos/sonar-con-ratas",
        lazy: loadDefault(() => import("./pages/SonarConRatas")),
      },
      {
        path: "significado-de-los-suenos/:slug",
        lazy: loadDefault(() => import("./pages/SignificadoSuenoDetalle")),
        getStaticPaths: async () => {
          const { DREAM_SEO_PAGES } = await import("@/lib/dreamSeo");
          return DREAM_SEO_PAGES.filter((page) => isDreamSeoSsable(page.slug)).map(
            (page) => `significado-de-los-suenos/${page.slug}`
          );
        },
      },

      {
        path: "login",
        lazy: loadDefault(() => import("./pages/Login")),
      },
      {
        path: "registro",
        lazy: loadDefault(() => import("./pages/Register")),
      },
      {
        path: "recuperar-contrasena",
        lazy: loadDefault(() => import("./pages/RecuperarContrasena")),
      },
      {
        path: "restablecer-contrasena",
        lazy: loadDefault(() => import("./pages/RestablecerContrasena")),
      },
      {
        path: "auth/callback",
        lazy: loadDefault(() => import("./pages/AuthCallback")),
      },

      {
        path: "privado",
        element: (
          <RequireAuth>
            <PrivateLayoutLazy />
          </RequireAuth>
        ),
        children: [
          {
            index: true,
            lazy: loadDefault(() => import("./pages/privado/Dashboard")),
          },
          {
            path: "mi-cuenta",
            lazy: loadDefault(() => import("./pages/privado/MiCuenta")),
          },
          {
            path: "mis-cartas",
            lazy: loadDefault(() => import("./pages/privado/MisCartas")),
          },
          {
            path: "mis-servicios",
            lazy: loadDefault(() => import("./pages/privado/MisServicios")),
          },
          {
            path: "mis-servicios/:id",
            lazy: loadDefault(() => import("./pages/privado/ServicioConversacion")),
          },
          {
            path: "solicitar",
            lazy: loadDefault(() => import("./pages/privado/Solicitar")),
          },
          {
            path: "admin",
            lazy: loadDefault(() => import("./pages/privado/admin/AdminDashboard")),
          },
          {
            path: "admin/clientes",
            lazy: loadDefault(() => import("./pages/privado/admin/AdminClientes")),
          },
          {
            path: "admin/clientes/:id",
            lazy: loadDefault(() => import("./pages/privado/admin/AdminClienteDetalle")),
          },
          {
            path: "admin/servicios/:id",
            lazy: loadDefault(() => import("./pages/privado/admin/AdminServicioDetalle")),
          },
          {
            path: "admin/testimonios",
            lazy: loadDefault(() => import("./pages/privado/admin/AdminTestimonios")),
          },
        ],
      },

      {
        path: "*",
        lazy: loadDefault(() => import("./pages/NotFound")),
      },
    ],
  },
];

const PrivateLayout = lazyWithRetry(() => import("./pages/privado/Layout"));
function PrivateLayoutLazy() {
  return (
    <Suspense fallback={null}>
      <PrivateLayout />
    </Suspense>
  );
}
